import { appPath, defectsPath } from "helpers/routePaths";

// Single owner of the Defect Next/Previous navigation contract shared by the
// Defects list, the Service Defects tab, and the Defect detail page. Each
// entry point caches its full ordered id list in sessionStorage under a fixed
// key; the URL carries only a mode flag plus a display pos. Position is
// always derived from the current defect id via indexOf, so a stale URL can
// hide the navigation but never step through the wrong list.

const LIST_NAV_KEY = "defectNav";
const SERVICE_KEY_PREFIX = "serviceDefectNav:";

// Matches the backend's SearchConstants.MAX_NAVIGATION_IDS cap on the
// Defects/Search/ids endpoint. The Service defect list has no server-side
// cap, so this bounds the sessionStorage entry it caches; beyond it the list
// is left uncached and Next/Previous stays hidden.
export const MAX_DEFECT_NAV_IDS = 5000;

const writeNavIds = (navKey, ids) => {
	try {
		sessionStorage.setItem(navKey, JSON.stringify(ids));
		return true;
	} catch {
		// Storage full/unavailable - navigation is simply unavailable.
		return false;
	}
};

export const saveDefectListNavIds = (ids) => {
	if (!Array.isArray(ids)) return false;
	return writeNavIds(LIST_NAV_KEY, ids);
};

export const cacheServiceDefectNavIds = (serviceId, ids) => {
	if (!Array.isArray(ids) || !ids.length || ids.length > MAX_DEFECT_NAV_IDS) {
		return false;
	}
	const navKey = `${SERVICE_KEY_PREFIX}${serviceId}`;
	try {
		for (let i = sessionStorage.length - 1; i >= 0; i--) {
			const key = sessionStorage.key(i);
			if (key && key.startsWith(SERVICE_KEY_PREFIX) && key !== navKey) {
				sessionStorage.removeItem(key);
			}
		}
	} catch {
		return false;
	}
	return writeNavIds(navKey, ids);
};

export const readDefectNavIds = (navKey) => {
	try {
		const idList = JSON.parse(sessionStorage.getItem(navKey) ?? "null");
		return Array.isArray(idList) && idList.length ? idList : null;
	} catch {
		return null;
	}
};

export const buildDefectUrl = (id) => `${appPath}${defectsPath}/${id}`;

// The pos param is display-only, 1-based to match the "12/1397" pill, and is
// never trusted on read: position always comes from indexOf(id) against the
// cached list.
export const buildDefectListUrl = (id, ids) => {
	const pos = Array.isArray(ids) ? ids.indexOf(id) : -1;
	if (pos === -1) return buildDefectUrl(id);
	return `${buildDefectUrl(id)}?fromDefectsList=1&pos=${pos + 1}`;
};

export const buildServiceDefectUrl = (id, serviceId, pos) => {
	const base = `${buildDefectUrl(id)}?fromServiceId=${serviceId}`;
	if (pos === null || pos === undefined) return base;
	return `${base}&pos=${pos + 1}`;
};

export const buildNavUrl = (navContext, targetId, targetPos) => {
	if (!navContext) return buildDefectUrl(targetId);
	if (navContext.mode === "list") {
		return `${buildDefectUrl(targetId)}?fromDefectsList=1&pos=${targetPos + 1}`;
	}
	return buildServiceDefectUrl(targetId, navContext.serviceId, targetPos);
};

export const parseDefectNavContext = (searchParams, currentDefectId) => {
	const id = Number(currentDefectId);
	if (!Number.isFinite(id)) return null;

	let mode;
	let serviceId;
	let navKey;
	if (searchParams.get("fromDefectsList")) {
		mode = "list";
		navKey = LIST_NAV_KEY;
	} else if (searchParams.get("fromServiceId")) {
		mode = "service";
		serviceId = searchParams.get("fromServiceId");
		navKey = `${SERVICE_KEY_PREFIX}${serviceId}`;
	} else {
		return null;
	}

	const idList = readDefectNavIds(navKey);
	if (!idList) return null;

	// The URL's pos is display-only; deriving the real position from the
	// current defect id means a stale or hand-edited link can never navigate
	// relative to the wrong position.
	const pos = idList.indexOf(id);
	if (pos === -1) return null;

	return { idList, pos, mode, serviceId };
};
